import * as React from "react";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import { Box } from "@mui/material";
import {
    AdvancedSavedSearchItem,
    RegistrationSavedSearchItem,
    SessionSavedSearchItem,
} from "../../../services/types/enterpriseCoachingApiTypes";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import { useAdvancedRegistrationSearchMutation } from "../../../services/cclTokenedEnterpriseParticipantApi";
import { useLazyGetAssessmentAdminGroupByEmailQuery } from "../../../services/cclTokenedAssessmentApi";
import {
    AdvancedSearchRegistrationParameters,
    AdvancedSearchRequest,
    AdvancedSearchSessionParameters,
    ParticipantSearchResult,
} from "../../../services/types/enterpriseParticipantApiTypes";
import RegistrationSearchCard from "./registrationSearchCard";
import SessionSearchCard from "./sessionsSearchCard";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { getUtcDateStringWithoutOffset } from "../../../utilities/dateHelpers";

interface RegistrationAdvancedSearchControlProps {
    onSearchResults: (participants: ParticipantSearchResult[]) => void;
    updateCurrentSearch: (search: any) => void;
    pastSearch: AdvancedSavedSearchItem;
    open: boolean;
    onClose: () => void;
}

export interface AttributeSearch {
    attributeType: AutocompleteItem | undefined;
    attributeValue: AutocompleteItem | undefined;
    attributeLevel: AutocompleteItem | undefined;
    id: string;
}

export interface AutocompleteItem {
    id: string;
    label: string;
}

const RegistrationAdvancedSearchControl: React.FC<RegistrationAdvancedSearchControlProps> = (
    props,
) => {
    const [runAdvancedSearch, { data: searchData, isLoading }] =
        useAdvancedRegistrationSearchMutation();
    const [getAdminGroupByEmail, { isLoading: adminEmailLookupLoading }] =
        useLazyGetAssessmentAdminGroupByEmailQuery();
    const [openSnackbar, setOpenSnackbar] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>("");
    const [severity, setSeverity] = React.useState<"error" | "success" | undefined>(undefined);
    const { logEvent } = useLogAccessEvent();

    React.useEffect(() => {
        if (searchData && searchData.success) {
            props.onSearchResults(searchData.participants);
        } else if (searchData && !searchData.success) {
            console.log("Search Error: ", searchData.message);
        }
    }, [props, searchData]);

    const executeSearch = (adminGroupKey?: number) => {
        const registrationObj: AdvancedSearchRegistrationParameters = {
            status: props.pastSearch.registration.participantStatus
                ? +props.pastSearch.registration.participantStatus.id
                : undefined,
            firstName: props.pastSearch.registration.firstName
                ? props.pastSearch.registration.firstName.label
                : undefined,
            lastName: props.pastSearch.registration.lastName
                ? props.pastSearch.registration.lastName.label
                : undefined,
            email: props.pastSearch.registration.email
                ? props.pastSearch.registration.email.label.toLowerCase()
                : undefined,
        };
        const sessionObj: AdvancedSearchSessionParameters = {
            type: props.pastSearch.session.type ? +props.pastSearch.session.type.id : undefined,
            status: props.pastSearch.session.status
                ? +props.pastSearch.session.status.id
                : undefined,
            projectId: props.pastSearch.session.projectId
                ? props.pastSearch.session.projectId.label
                : undefined,
            sessionId: props.pastSearch.session.sessionId
                ? props.pastSearch.session.sessionId.label
                : undefined,
            title: props.pastSearch.session.title
                ? props.pastSearch.session.title.label
                : undefined,
            customer: props.pastSearch.session.customer
                ? props.pastSearch.session.customer.label
                : undefined,
            managingLocation: props.pastSearch.session.managingLocation
                ? +props.pastSearch.session.managingLocation.id
                : undefined,
            startDateFrom: props.pastSearch.session.startDateFrom
                ? getUtcDateStringWithoutOffset(
                      new Date(props.pastSearch.session.startDateFrom.label),
                  )
                : undefined,
            startDateTo: props.pastSearch.session.startDateTo
                ? getUtcDateStringWithoutOffset(
                      new Date(props.pastSearch.session.startDateTo.label),
                  )
                : undefined,
            aagKey:
                adminGroupKey ??
                (props.pastSearch.session.administratorGroup
                    ? +props.pastSearch.session.administratorGroup.id
                    : undefined),
        };

        for (const key in registrationObj) {
            if (registrationObj[key as keyof AdvancedSearchRegistrationParameters] === "") {
                registrationObj[key as keyof AdvancedSearchRegistrationParameters] = undefined;
            }
        }

        for (const key in sessionObj) {
            if (sessionObj[key as keyof AdvancedSearchSessionParameters] === "") {
                sessionObj[key as keyof AdvancedSearchSessionParameters] = undefined;
            }
        }

        const searchObj: AdvancedSearchRequest = {
            registrationParameters: registrationObj,
            sessionParameters: sessionObj,
        };

        runAdvancedSearch(searchObj)
            .unwrap()
            .then((res) => {
                if (res && res.success) {
                    props.onSearchResults(res.participants);
                    setMessage("Search completed");
                    setSeverity("success");
                    setOpenSnackbar(true);
                    logEvent("RegistrationSearchExecuted", null);
                } else if (res && !res.success) {
                    console.log("Search Error: ", res.message);
                    if (res.message.toLowerCase().startsWith("too many")) {
                        setMessage(res.message);
                    } else {
                        setMessage("Search failed");
                    }
                    setSeverity("error");
                    setOpenSnackbar(true);
                }
            });
    };

    const handleSearch = () => {
        if (!props.pastSearch) {
            console.log("Error, no search parameters found");
            return;
        }

        const adminEmail = props.pastSearch.session.administratorEmail?.label?.trim()?.toLowerCase();
        if (adminEmail) {
            getAdminGroupByEmail(adminEmail)
                .unwrap()
                .then((groups) => {
                    if (!groups || groups.length === 0) {
                        setMessage("No administrator group found for the specified email.");
                        setSeverity("error");
                        setOpenSnackbar(true);
                        return;
                    }
                    executeSearch(groups[0].groupId);
                })
                .catch(() => {
                    setMessage("No administrator group found for the specified email.");
                    setSeverity("error");
                    setOpenSnackbar(true);
                });
            return;
        }

        executeSearch();
    };

    const handleReset = () => {
        const sessionObj = {} as SessionSavedSearchItem;
        const registrationObj = {} as RegistrationSavedSearchItem;
        props.updateCurrentSearch({
            session: sessionObj,
            registration: registrationObj,
        });
        props.onClose();
    };

    return (
        <CclDrawer
            title={"Advanced Search"}
            open={props.open}
            onClose={props.onClose}
            width="standard"
        >
            <Box sx={{ overflow: "hidden", height: 1 }}>
                {openSnackbar && (
                    <CclAlertSnackbar
                        open={openSnackbar}
                        onClose={() => setOpenSnackbar(false)}
                        message={message}
                        severity={severity}
                    />
                )}
                <Box sx={{ overflow: "auto" }}>
                    <RegistrationSearchCard
                        updateCurrentSearch={props.updateCurrentSearch}
                        pastSearch={props.pastSearch}
                    />
                    <SessionSearchCard
                        updateCurrentSearch={props.updateCurrentSearch}
                        pastSearch={props.pastSearch}
                    />
                </Box>

                <CclDrawerActionBar>
                    <CclLoadingButton
                        loading={isLoading || adminEmailLookupLoading}
                        onClick={handleSearch}
                        disabled={false}
                        sx={{ m: 1 }}
                        mode={"primary"}
                    >
                        Search
                    </CclLoadingButton>
                    <CclButton sx={{ m: 1 }} onClick={props.onClose} mode={"secondary"}>
                        Close
                    </CclButton>
                    <CclButton
                        mode={"primary"}
                        sx={{ float: "right", m: 1, ml: "auto" }}
                        onClick={handleReset}
                    >
                        Reset
                    </CclButton>
                </CclDrawerActionBar>
            </Box>
        </CclDrawer>
    );
};

export default RegistrationAdvancedSearchControl;
